// Upscales the passport MRZ strip before the general OCR reads it (#245).
//
// - In-page iPhone photos are video frames (at most 1920 px, useSecureCamera.js), so the
//   two-page MRZ strip is only about 750 x 140 px and the OCR often split the top line.
// - Staging iPhone, 7 photos: two 44-character lines on 3 of 7 strips as-is, 6 of 7 at 2x
//   (a staging test read with the same canvas steps as below).
// - Only the general OCR gets the 2x image. The MRZ recognizer and the uploaded mrzImage
//   keep the original strip.
// - Never throws: without a 2x image it returns the strip as-is and the reason.

export const MRZ_OCR_SCALE = 2;
// iPhone strips are at most about 860,000 px at 2x. Bigger strips (large Android photos)
// keep the as-is read, as before: this bounds the OCR time and memory.
export const MAX_UPSCALED_PIXELS = 2000000;

// Error class name only, never the message.
export function errorClassName(e) {
  return e && typeof e.name === "string" ? e.name.slice(0, 40) : "unknown";
}

function asIs(strip, skipped, error = null) {
  const out = { image: strip, scale: 1, skipped };
  if (error) out.errorName = errorClassName(error);
  return out;
}

// Returns { image: ImageData, scale } or { image: strip, scale: 1, skipped, errorName? }.
export async function upscaleMrzStrip(strip, scale = MRZ_OCR_SCALE) {
  if (typeof createImageBitmap !== "function") return asIs(strip, "unsupported");
  let bitmap = null;
  let canvas = null;
  try {
    bitmap = await createImageBitmap(new Blob([strip], { type: "image/jpeg" }));
    const width = bitmap.width * scale;
    const height = bitmap.height * scale;
    if (width * height > MAX_UPSCALED_PIXELS) return asIs(strip, "too_large");
    canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx2d = canvas.getContext("2d");
    if (!ctx2d) return asIs(strip, "no_canvas");
    ctx2d.fillStyle = "#ffffff";
    ctx2d.fillRect(0, 0, width, height);
    ctx2d.imageSmoothingQuality = "high";
    ctx2d.drawImage(bitmap, 0, 0, width, height);
    return { image: ctx2d.getImageData(0, 0, width, height), scale };
  } catch (e) {
    return asIs(strip, "error", e);
  } finally {
    if (bitmap && typeof bitmap.close === "function") bitmap.close();
    if (canvas) {
      canvas.width = 0; // free the canvas memory now (iOS Safari holds it otherwise)
      canvas.height = 0;
    }
  }
}
